import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import ReactPaginate from 'react-paginate';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { formatDateTime } from '../../../utils/dateTimeFormate';
import { AuthContext } from '../../../context/AuthContext';
import { getCmTrophyApplicants, downloadCmTrophyExport, sendCmTrophyApplicationsForward } from '../../../services/adminApiService/cmTrophyApi/cmTrophyApi';
import FilterNumber from '../../../jsx/components/FilterNumberDropDown/FilterNumber';
import SearchFilter from '../../../jsx/components/SearchFilter/SearchFilter';
import useSearchValue from '../../../hooks/useSearchValue';
import RefreshButton from '../../../jsx/components/RefreshButton/RefreshButton';
import CmTrophySportFilter from '../cmTrophySportFilter/CmTrophySportFilter';
import CmTrophyAgeCategoryFilter from '../cmTrophyAgeCategoryFilter/CmTrophyAgeCategoryFilter';
import CmTrophySansadFilter from '../cmTrophySansadFilter/CmTrophySansadFilter';
import CmTrophyVidhanSabhaFilter from '../cmTrophyVidhanSabhaFilter/CmTrophyVidhanSabhaFilter';
import CmTrophyNyayPanchayatFilter from '../cmTrophyNyayPanchayatFilter/CmTrophyNyayPanchayatFilter';
import ConfirmModal from '../editProfile/ConfirmModal';
import DistrictFilter from '../districtFilter/DistrictFilter';
import BlockFilter from '../districtFilter/BlockFilter';
import { LocationContext } from '../../../context/LocationContext';
import { CM_TROPHY_STAGE_BY_LEVEL } from './cmTrophyPermissions';

const CmTrophyApplicationsTable = () => {
    const navigate = useNavigate();
    const { userData } = useContext(AuthContext);
    const { selectedDistrictId, setSelectedDistrictId, selectedBlockId, setSelectedBlockId } = useContext(LocationContext);
    const userLevel = Number(userData?.user_level);
    const ownStage = CM_TROPHY_STAGE_BY_LEVEL[userLevel];
    const canForward = ownStage === 'nyay_panchayat' || ownStage === 'vidhan_sabha' || ownStage === 'sansad';
    const [applicantList, setApplicantList] = useState({ data: [], total: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const { searchValue, setSearchValue, debouncedSearchValue, setDebouncedSearchValue } = useSearchValue();
    const [sport, setSport] = useState("");
    const [gender, setGender] = useState("");
    const [ageCategory, setAgeCategory] = useState("");
    const [registrationLevel, setRegistrationLevel] = useState("");
    const [sansadId, setSansadId] = useState("");
    const [vidhanSabhaId, setVidhanSabhaId] = useState("");
    const [nyayPanchayatId, setNyayPanchayatId] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [showForwardConfirm, setShowForwardConfirm] = useState(false);
    const [forwarding, setForwarding] = useState(false);
    const tableScrollRef = useRef(null);
    const topScrollRef = useRef(null);
    const [tableScrollWidth, setTableScrollWidth] = useState(0);

    const filters = {
        search: debouncedSearchValue,
        sport,
        gender,
        ageCategory,
        registrationLevel,
        sansadId,
        vidhanSabhaId,
        nyayPanchayatId,
        dateFrom,
        dateTo,
        districtId: selectedDistrictId,
        blockId: selectedBlockId,
    };

    const fetchApplicants = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getCmTrophyApplicants({ page: currentPage, perPage, ...filters });
            setApplicantList(response?.data?.data || { data: [], total: 0 });
        } catch (error) {
            console.error('CM Trophy applicants error:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, debouncedSearchValue, sport, gender, ageCategory, registrationLevel, sansadId, vidhanSabhaId, nyayPanchayatId, dateFrom, dateTo, selectedDistrictId, selectedBlockId]);

    useEffect(() => {
        fetchApplicants();
    }, [fetchApplicants]);

    // LocationContext's selected district/block are global/app-wide - reset on mount so
    // a selection left over from another page doesn't silently filter this table.
    useEffect(() => {
        setSelectedDistrictId("");
        setSelectedBlockId("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (event) => {
        setCurrentPage(event.selected + 1);
    };

    const handleSansadChange = (val) => {
        setSansadId(val);
        setVidhanSabhaId("");
        setNyayPanchayatId("");
        setCurrentPage(1);
    };

    const handleVidhanSabhaChange = (val) => {
        setVidhanSabhaId(val);
        setNyayPanchayatId("");
        setCurrentPage(1);
    };

    const handleReset = () => {
        setSearchValue("");
        setDebouncedSearchValue("");
        setSport("");
        setGender("");
        setAgeCategory("");
        setRegistrationLevel("");
        setSansadId("");
        setVidhanSabhaId("");
        setNyayPanchayatId("");
        setDateFrom("");
        setDateTo("");
        setSelectedDistrictId("");
        setSelectedBlockId("");
        setPerPage(10);
        setCurrentPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadCmTrophyExport(filters);
        } catch (error) {
            console.error('CM Trophy export error:', error);
        } finally {
            setExporting(false);
        }
    };

    const rows = applicantList?.data || [];

    useEffect(() => {
        setSelectedIds([]);
    }, [rows]);

    const forwardableIds = rows.filter(app => app.current_stage === ownStage).map(app => app.id);
    const allForwardableSelected = forwardableIds.length > 0 && forwardableIds.every(id => selectedIds.includes(id));

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        setSelectedIds(allForwardableSelected ? [] : forwardableIds);
    };

    const handleSendForward = async () => {
        setForwarding(true);
        try {
            const res = await sendCmTrophyApplicationsForward(selectedIds);
            const data = res?.data?.data || {};
            const failedEntries = Object.entries(data.failed || {});
            if (failedEntries.length > 0) {
                const details = failedEntries.map(([id, reason]) => `#${id}: ${reason}`).join('\n');
                swal('Some applications were not forwarded', details, 'warning');
            } else {
                swal('Forwarded', 'Selected applications were sent forward successfully.', 'success');
            }
            setSelectedIds([]);
            setShowForwardConfirm(false);
            fetchApplicants();
        } catch (error) {
            swal('Error', error?.response?.data?.message || 'Failed to send applications forward.', 'error');
        } finally {
            setForwarding(false);
        }
    };

    useEffect(() => {
        setTableScrollWidth(tableScrollRef.current?.scrollWidth || 0);
    }, [applicantList, canForward]);

    const syncTopScroll = () => {
        if (tableScrollRef.current && topScrollRef.current) {
            tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };
    const syncTableScroll = () => {
        if (tableScrollRef.current && topScrollRef.current) {
            topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
        }
    };

    return (
        <div className="col-12">
            <div className="card filter-card-wrapper">
                <div className="px-3 d-flex justify-content-between flex-wrap">
                    <div className="d-flex mt-3 mx-2 flex-wrap">
                        <div>
                            <FilterNumber value={perPage} onChange={(val) => {
                                setPerPage(val);
                                setCurrentPage(1)
                            }} />
                        </div>
                        <div>
                            <SearchFilter searchValue={searchValue} setSearchValue={setSearchValue} setCurrentPage={setCurrentPage} />
                        </div>
                    </div>
                    <div className="d-flex mt-3">
                        {canForward && (
                            <div className="me-2">
                                <Button
                                    variant="primary"
                                    onClick={() => setShowForwardConfirm(true)}
                                    disabled={selectedIds.length === 0 || forwarding}
                                >
                                    {forwarding ? 'Forwarding...' : `Send Forward${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
                                </Button>
                            </div>
                        )}
                        <div className="me-2">
                            <Button variant="outline-primary" onClick={handleExport} disabled={exporting}>
                                {exporting ? 'Exporting...' : 'Export'}
                            </Button>
                        </div>
                        <div>
                            <RefreshButton refreshCallback={handleReset} />
                        </div>
                    </div>
                </div>

                <div className="mx-4 d-flex mt-2 flex-wrap">
                    <div className="me-2">
                        <CmTrophySportFilter value={sport} onChange={(val) => { setSport(val); setCurrentPage(1); }} />
                    </div>
                    <div className="me-2">
                        <Form.Select
                            value={gender}
                            onChange={(e) => { setGender(e.target.value); setCurrentPage(1); }}
                            className="me-2"
                            style={{ width: "100%", padding: "11px 50px 10px 11px" }}
                        >
                            <option value="">All Genders</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                        </Form.Select>
                    </div>
                    <div className="me-2">
                        <CmTrophyAgeCategoryFilter value={ageCategory} onChange={(val) => { setAgeCategory(val); setCurrentPage(1); }} />
                    </div>
                    <div className="me-2">
                        <Form.Select
                            value={registrationLevel}
                            onChange={(e) => { setRegistrationLevel(e.target.value); setCurrentPage(1); }}
                            className="me-2"
                            style={{ width: "100%", padding: "11px 50px 10px 11px" }}
                        >
                            <option value="">All Registration Levels</option>
                            <option value="state">State</option>
                            <option value="sansad">Sansad</option>
                            <option value="vidhan_sabha">Vidhan Sabha</option>
                            <option value="nyay_panchayat">Nyay Panchayat</option>
                        </Form.Select>
                    </div>
                    <div className="me-2">
                        <CmTrophySansadFilter value={sansadId} onChange={handleSansadChange} />
                    </div>
                    <div className="me-2">
                        <CmTrophyVidhanSabhaFilter value={vidhanSabhaId} onChange={handleVidhanSabhaChange} sansadId={sansadId} />
                    </div>
                    <div className="me-2">
                        <CmTrophyNyayPanchayatFilter value={nyayPanchayatId} onChange={(val) => { setNyayPanchayatId(val); setCurrentPage(1); }} vidhanSabhaId={vidhanSabhaId} />
                    </div>
                    <div className="me-2">
                        <Form.Control
                            type="date"
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                            style={{ width: "100%", padding: "11px" }}
                        />
                    </div>
                    <div className="me-2">
                        <Form.Control
                            type="date"
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                            style={{ width: "100%", padding: "11px" }}
                        />
                    </div>
                    <div className="me-2">
                        <DistrictFilter setCurrentPage={setCurrentPage} />
                    </div>
                    <div className="me-2">
                        <BlockFilter setCurrentPage={setCurrentPage} />
                    </div>
                </div>

                <div className="card-body">
                    {loading ? (
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                        <div
                            ref={topScrollRef}
                            onScroll={syncTopScroll}
                            style={{ overflowX: 'auto', overflowY: 'hidden' }}
                        >
                            <div style={{ width: tableScrollWidth, height: 1 }} />
                        </div>
                        <div className="w-100 table-responsive" ref={tableScrollRef} onScroll={syncTableScroll}>
                            <div id="cm_trophy_table_wrapper" className="dataTables_wrapper">
                                <table id="cm_trophy_table" className="display w-100 dataTable">
                                    <thead>
                                        <tr>
                                            {canForward && (
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        checked={allForwardableSelected}
                                                        onChange={toggleSelectAll}
                                                        disabled={forwardableIds.length === 0}
                                                    />
                                                </th>
                                            )}
                            <th>Sr No</th>
                                            <th>Rejection Reason</th>
                                            <th>Image</th>
                                            <th>Name</th>
                                            <th>Application Code</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>DOB</th>
                                            <th>Gender</th>
                                            <th>Sport</th>
                                            <th>Events</th>
                                            <th>Age Category</th>
                                            <th>Aadhar Number</th>
                                            <th>Date of Registration</th>
                                            <th>District</th>
                                            <th>Block</th>
                                            <th>Father Name</th>
                                            <th>Mother Name</th>
                                            <th>Registration Level</th>
                                            <th>Sansad</th>
                                            <th>Vidhan Sabha</th>
                                            <th>Nyay Panchayat</th>
                                            <th>Stage</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length > 0 ? (
                                            rows.map((data, index) => {
                                                const serialNumber = (currentPage - 1) * perPage + (index + 1);
                                                return (
                                                    <tr key={data?.id ?? index}>
                                                        {canForward && (
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedIds.includes(data.id)}
                                                                    disabled={data.current_stage !== ownStage}
                                                                    onChange={() => toggleSelect(data.id)}
                                                                />
                                                            </td>
                                                        )}
                                                        <td>{serialNumber}</td>
                                                        <td>{data?.status === 'rejected' ? (data?.admin_remark || '-') : '-'}</td>
                                                        <td>
                                                            {data?.docs?.[0]?.file_url ? (
                                                                <img
                                                                    src={data.docs[0].file_url}
                                                                    alt={data?.name}
                                                                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
                                                                />
                                                            ) : (
                                                                <div style={{ width: 40, height: 40, borderRadius: 6, background: '#f1f1f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <i className="bi bi-person" style={{ color: '#8757e8' }} />
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>{data?.name}</td>
                                                        <td>{data?.application_code}</td>
                                                        <td>{data?.email}</td>
                                                        <td>{data?.phone}</td>
                                                        <td>{data?.dob ? formatDateTime(data.dob) : ''}</td>
                                                        <td>{data?.gender}</td>
                                                        <td>{data?.sport}</td>
                                                        <td>{Array.isArray(data?.events) ? data.events.join(', ') : (data?.events || '')}</td>
                                                        <td>{data?.age_category}</td>
                                                        <td>{data?.aadhar_number}</td>
                                                        <td>{data?.created_at ? formatDateTime(data.created_at) : ''}</td>
                                                        <td>{data?.district?.name}</td>
                                                        <td>{data?.block?.name}</td>
                                                        <td>{data?.father_name}</td>
                                                        <td>{data?.mother_name}</td>
                                                        <td>{data?.registration_level}</td>
                                                        <td>{data?.sansad?.name}</td>
                                                        <td>{data?.vidhan_sabha?.name}</td>
                                                        <td>{data?.nyay_panchayat?.name}</td>
                                                        <td style={{ textTransform: 'capitalize' }}>{data?.current_stage?.replace(/_/g, ' ')}</td>
                                                        <td>{data?.status}</td>
                                                        <td>
                                                            <div className="d-flex">
                                                                <Button style={{ whiteSpace: 'nowrap' }} onClick={() => navigate(`/cm-trophy/applications/${data?.id}`)}> View </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={canForward ? 26 : 25} className="text-center">Data is not available</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        </>
                    )}
                </div>
            </div>
            <ReactPaginate
                nextLabel="next >"
                onPageChange={handlePageChange}
                pageRangeDisplayed={8}
                marginPagesDisplayed={7}
                pageCount={Math.ceil((applicantList?.total || 0) / perPage)}
                previousLabel="< previous"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                breakLabel="..."
                breakClassName="page-item"
                breakLinkClassName="page-link"
                containerClassName="pagination d-flex flex-wrap"
                activeClassName="active"
                renderOnZeroPageCount={null}
                forcePage={currentPage - 1}
            />

            {canForward && (
                <ConfirmModal
                    type="forward"
                    show={showForwardConfirm}
                    handleClose={() => setShowForwardConfirm(false)}
                    message={`Are you sure you want to send ${selectedIds.length} selected application${selectedIds.length === 1 ? '' : 's'} forward to the next level?`}
                    onConfirm={handleSendForward}
                />
            )}
        </div>
    )
}

export default CmTrophyApplicationsTable
